import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Organisation, OrganisationState } from '../../types/organization';
import { OrganisationService } from '../../services/organisation/organizationService';

const initialState: OrganisationState = {
  items: [],
  selectedOrganisation: null,
  isLoading: false,
  error: null,
};

export const fetchOrganisations = createAsyncThunk(
  'organizations/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const results = await OrganisationService.getOrganisations();
      return results;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch organizations'
      );
    }
  }
);

export const fetchOrganiationsById = createAsyncThunk(
  'organizations/fetchOrganiationsById',
  async (clientId: string, { rejectWithValue }) => {
    try {
      const results = await OrganisationService.getClientOrganisation(clientId);
      return results;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch organizations'
      );
    }
  }
);

const organizationSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    setSelectedOrganisation: (state, action: PayloadAction<Organisation | null>) => {
      state.selectedOrganisation = action.payload;
    },
    deleteOrganisation: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(org => org.id !== action.payload);
    },
    updateOrganisationField: (
      state,
      action: PayloadAction<{ field: keyof Organisation; value: any }>
    ) => {
      if (state.selectedOrganisation) {
        (state.selectedOrganisation[action.payload.field] as any) = action.payload.value;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchOrganisations.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganisations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.error = null;

        // Update the selectedOrganisation if it exists in the fetched data
        if (state.selectedOrganisation) {
          const updatedSelectedOrg = action.payload.find(
            (org: Organisation) => org.id === state.selectedOrganisation?.id
          );
          if (updatedSelectedOrg) {
            state.selectedOrganisation = updatedSelectedOrg;
          }
        }
      })
      .addCase(fetchOrganisations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch organizations';
      })
      .addCase(fetchOrganiationsById.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganiationsById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedOrganisation = action.payload[0];
        state.error = null;
      })
      .addCase(fetchOrganiationsById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch organizations';
      });
  },
});

// Selector to get the selected organization name
export const selectSelectedOrganisationName = (state: { organizations: OrganisationState }) =>
  state.organizations.selectedOrganisation
    ? state.organizations.selectedOrganisation.companyName + ' Dashboard'
    : 'CACXI Dashboard';

export const { setSelectedOrganisation, deleteOrganisation, updateOrganisationField } =
  organizationSlice.actions;
export default organizationSlice.reducer;
